import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const callFunction = async <T>(name: string, data: any): Promise<T> => {
  const fn = httpsCallable(functions, name);
  const result = await fn(data);
  return result.data as T;
};

export interface SocialConnections {
  x: { username: string } | null;
  threads: { username: string } | null;
  linkedin: { username: string } | null;
}

export type SocialPlatform = 'x' | 'threads' | 'linkedin';

export const generateSocialPost = async (
  title: string,
  content: string,
  language: string,
  platform: SocialPlatform,
  noteLink: string,
  tone: 'default' | 'shorter' | 'technical' | 'beginner' = 'default',
): Promise<{ post: string; hashtags: string[] }> => {
  return callFunction('generateSocialPost', { title, content, language, platform, noteLink, tone });
};

export const socialOAuthInit = async (platform: SocialPlatform): Promise<{ authUrl: string }> => {
  return callFunction('socialOAuthInit', { platform });
};

export const getSocialConnections = async (): Promise<SocialConnections> => {
  return callFunction('getSocialConnections', {});
};

export const disconnectSocial = async (platform: SocialPlatform): Promise<void> => {
  await callFunction('disconnectSocial', { platform });
};

export const publishSocialPost = async (
  platform: SocialPlatform,
  text: string,
  imageUrls?: string[],
  topicTag?: string,
): Promise<{ postUrl: string; postId: string }> => {
  return callFunction('publishSocialPost', { platform, text, imageUrls, topicTag });
};

export interface ShareSuggestion {
  style: 'diary' | 'concept' | 'synthesis' | 'progress';
  title: string;
  postText: string;
  includedNodeIds: string[];
  primaryNodeId?: string;
  reason: string;
  hashtags: string[];
  confidence: 'high' | 'medium' | 'low';
}

export interface ShareNotRecommended {
  nodeId: string;
  title: string;
  reason: string;
}

export interface PostAngleScore {
  angle: string;
  score: number;
  reason: string;
  suggestedHook: string;
  draftStrategy?: string;
}

export const generateShareAdvice = async (params: {
  notes: { id: string; title: string; content: string; bestAngle?: string; bestAngleScore?: number; suggestedHook?: string }[];
  notebookTitle: string;
  lastPublishUrl?: string;
  language: string;
}): Promise<{ suggestions: ShareSuggestion[]; notRecommended: ShareNotRecommended[] }> => {
  return callFunction('generateShareAdvice', params);
};

export const scoreNoteForThreads = async (params: {
  notebookId: string;
  nodeId: string;
  title: string;
  content: string;
  language?: string;
}): Promise<{ score: number; reason: string; correctnessScore: number | null; correctnessAccurate: boolean | null; correctnessFeedback: string; makeItPostworthy: string; angleScores: PostAngleScore[]; bestAngle: string; bestAngleScore: number | null }> => {
  return callFunction('scoreNoteForThreads', params);
};

export const scoreNoteBatch = async (params: {
  notebookId: string;
  notes: { id: string; title: string; content: string }[];
  language?: string;
}): Promise<{ processed: number; skipped: number }> => {
  const fn = httpsCallable(functions, 'scoreNoteBatch', { timeout: 540000 });
  const result = await fn(params);
  return result.data as { processed: number; skipped: number };
};

export const prepareDraftBatch = async (params: {
  notebookId: string;
  notes: { id: string; title: string; content: string; threadsPostDraftAt?: number }[];
  notebookTitle?: string;
  language?: string;
}): Promise<{ processed: number; skipped: number }> => {
  const fn = httpsCallable(functions, 'prepareDraftBatch', { timeout: 540000 });
  const result = await fn(params);
  return result.data as { processed: number; skipped: number };
};

export interface ScheduledPost {
  id: string;
  nodeId: string;
  notebookId: string;
  platform: 'threads' | 'x' | 'linkedin';
  postText: string;
  hashtags: string[];
  imageUrls?: string[];
  noteLink?: string;
  scheduledAt: number;
  status: 'pending' | 'posted' | 'failed' | 'cancelled';
  createdAt: number;
  postUrl?: string;
  postId?: string;
  failReason?: string;
  postedAt?: number;
  failedAt?: number;
}

export const schedulePost = async (params: {
  nodeId: string; notebookId: string; platform: string;
  postText: string; hashtags: string[]; imageUrls?: string[];
  noteLink?: string; firstCommentLink?: string; scheduledAt: number;
}): Promise<{ id: string }> => {
  return callFunction('schedulePost', params);
};

export const cancelScheduledPost = async (postId: string): Promise<void> => {
  await callFunction('cancelScheduledPost', { postId });
};

export const updateScheduledPost = async (postId: string, scheduledAt: number): Promise<void> => {
  await callFunction('updateScheduledPost', { postId, scheduledAt });
};

export const retryScheduledPost = async (postId: string): Promise<void> => {
  await callFunction('retryScheduledPost', { postId });
};

export const getScheduledPosts = async (): Promise<ScheduledPost[]> => {
  return callFunction('getScheduledPosts', {});
};

/** Open OAuth popup and wait for postMessage result. */
export function openOAuthPopup(authUrl: string): Promise<{ platform: string; username: string }> {
  return new Promise((resolve, reject) => {
    const popup = window.open(authUrl, 'social_oauth', 'width=600,height=700,scrollbars=yes');
    if (!popup) { reject(new Error('Popup blocked. Please allow popups for studyboard.app.')); return; }
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== 'https://studyboard.app' && e.origin !== window.location.origin) return;
      if (e.data?.type === 'SOCIAL_OAUTH_SUCCESS') {
        window.removeEventListener('message', onMessage);
        resolve({ platform: e.data.platform, username: e.data.username });
      } else if (e.data?.type === 'SOCIAL_OAUTH_ERROR') {
        window.removeEventListener('message', onMessage);
        reject(new Error(e.data.error || 'OAuth failed'));
      }
    };
    window.addEventListener('message', onMessage);
    const poll = setInterval(() => { if (popup.closed) { clearInterval(poll); window.removeEventListener('message', onMessage); reject(new Error('OAuth window closed')); } }, 500);
  });
}

const toSnakeCase = (s: string) =>
  (s.replace(/[^\w\s-]/g, '').trim().replace(/[-\s]+/g, '_').toLowerCase()) || 'untitled';

/**
 * Build a direct link to the exact node in the published GitHub file.
 * chapterTitle = L1 ancestor's title (determines the .md filename)
 * nodeId = the specific node's id (used as #node-{id} anchor)
 */
export function resolveNoteLink(
  notebookPublishUrl: string | null | undefined,
  chapterTitle?: string,
  nodeId?: string,
): string | null {
  if (!notebookPublishUrl) return null;
  const blobUrl = notebookPublishUrl.replace('/tree/', '/blob/');
  if (chapterTitle) {
    const fileUrl = `${blobUrl}/${toSnakeCase(chapterTitle)}.md`;
    return nodeId ? `${fileUrl}#node-${nodeId}` : fileUrl;
  }
  return blobUrl;
}

/** Web intent URL — opens platform with text pre-filled (no OAuth needed). */
export function intentUrl(platform: SocialPlatform, text: string): string {
  const encoded = encodeURIComponent(text);
  if (platform === 'x') return `https://x.com/intent/post?text=${encoded}`;
  if (platform === 'linkedin') return `https://www.linkedin.com/feed/`; // LinkedIn has no text intent URL
  return `https://www.threads.net/intent/post?text=${encoded}`;
}

export interface AdvisorMessage { role: 'user' | 'model'; text: string; }
export interface AdvisorNoteContext { title: string; score: number | null; tags: string[]; status: string; threadsStatus?: string; }
export interface AdvisorSelectedNote { title: string; content?: string; score: number | null; correctnessScore?: number | null; }

export async function threadsAdvisorChat(
  messages: AdvisorMessage[],
  notesContext: AdvisorNoteContext[],
  language: string,
  selectedNotes?: AdvisorSelectedNote[],
): Promise<string> {
  const data = await callFunction<{ reply: string }>('threadsAdvisorChat', { messages, notesContext, language, selectedNotes });
  return data.reply;
}
